import { IPersonaStyles } from '@fluentui/react';
import React, { FunctionComponent } from 'react';

import AppContext from 'App/AppContext';
import { IPIcon, IPScrollablePane, IPSpinner, IPText } from 'baseComponents';
import { IPList } from 'baseComponents/List/IPList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackItemStylesPaddingSceneScroll } from 'globalStyles';
import InternalAudit from 'models/internalAudit';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetActiveInternalAuditsForUser } from 'services/Api/internalAuditService';
import { apiRequest } from 'services/Auth/authConfig';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetActiveAuditsForUserProps extends IWidgetRendererProps {}

const WidgetActiveAuditsForUser: FunctionComponent<IWidgetActiveAuditsForUserProps> = (
  props: IWidgetActiveAuditsForUserProps,
) => {
  const { t } = useTranslation(['widgets', 'audit']);
  const appContext = React.useContext(AppContext);
  const history = useHistory();
  const [activeAudits, setActiveAudits] = React.useState<InternalAudit[] | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const audits = await apiGetActiveInternalAuditsForUser(accessToken);

      setActiveAudits(audits);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 20 } }}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={activeAudits}
            onRenderCell={(audit) => {
              if (!audit) return null;

              return (
                <IPPersona
                  styles={personaStyle}
                  size={IPPersonaSize.size40}
                  text={audit.name}
                  secondaryText={InternalAudit.getStatusText(audit.status, t)}
                  onRenderInitials={() => <IPIcon iconName="ComplianceAudit" />}
                  onClick={() => history.push(`/audit/${audit.internalAuditId}/execute`)}
                />
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {activeAudits?.length === 0 && (
        <IPStack.Item>
          <IPText>{t('widgets:ActiveAuditsForUser.Empty')}</IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default WidgetActiveAuditsForUser;
